import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  FormWebsiteAddForm,
  FormWebsiteAddInitialValues,
  FormWebsiteComponents,
  FormWebsiteAdditionalParams,
  FormWebsiteFormOptions,
  onFormWebsiteFormSaved,
  IFormWebsiteFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateFormWebsite, PermissionEnum, } from 'atlas-shared';
import { createFormWebsite, fetchFormWebsite, getFormWebsiteCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, useJourneies, useForms, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateFormWebsite> {}

const FormWebsiteFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const journeies = useJourneies();
  const forms = useForms();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateFormWebsite>>();

  const param_props: IFormWebsiteFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
    journeies: journeies.journeies,
    forms: forms.forms,
  };
  const formLayout = useMemo(() => FormWebsiteAddForm(values, param_props, t), [values, organizations, journeies, forms, t, abilities, ]);
  const options: IFormOptions = useMemo(() => FormWebsiteFormOptions(values || FormWebsiteAddInitialValues, param_props, t), [values, organizations, journeies, forms, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateFormWebsite, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateFormWebsite) => save(() => createFormWebsite(values).then(
    res => onSaved ? onSaved(res, navigate) : onFormWebsiteFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchFormWebsite(+id)
        .then(({
          organization_id,
          title,
          journey_id,
          form_id,
          description,
        }) => {
          setCloned({
            organization_id,
            title,
            journey_id,
            form_id,
            description,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !journeies.loaded || !forms.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.FormWebsite, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={FormWebsiteComponents}
        additionalParams={FormWebsiteAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...FormWebsiteAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('FORM_WEBSITE')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const FormWebsiteFormAddView = React.memo(withAdd(FormWebsiteFormAdd, getFormWebsiteCreateSchema, FormWebsiteAddInitialValues));
